// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {map, range, uniqueId} from 'lodash';
import classnames from 'classnames';

// Components
import Button from './button';

// Styles
import './pagination.scss';

export default class Pagination extends Component {
    getButtonClass(pageNumber) {
        const {currentPage = 1} = this.props;

        return classnames({
            current: currentPage === pageNumber,
            'pagination--button': true,
        });
    }

    getButtonProps(pageNumber) {
        return {
            'aria-label': `Page ${pageNumber}`,
            className: this.getButtonClass(pageNumber),
            onClick: this.handleClick,
            type: 'link',
            value: pageNumber,
        };
    }

    getClass() {
        const {className, inline} = this.props;

        return classnames(
            {
                pagination: true,
                pagination_inline: inline,
            },
            className
        );
    }

    getRange(position) {
        const {currentPage = 1, maxPages = 9, maxResultPages, totalPages} = this.props;
        const maxPageOffset = maxPages + 1;
        const middleOffset = Math.floor(maxPages / 2);
        const onlyStart = totalPages <= maxPages;
        const totalPagesLimited = totalPages - maxPages < middleOffset;
        const totalPagesResults = maxResultPages > 0 ? Math.min(maxResultPages, totalPages) : totalPages;
        let rangeResult = [];

        switch (position) {
            case 'start':
                if (onlyStart) {
                    rangeResult = range(1, totalPagesResults + 1);
                } else if (totalPagesResults > maxPages && currentPage < middleOffset) {
                    rangeResult = range(1, maxPageOffset - 1);
                } else {
                    rangeResult = [1];
                }

                break;

            case 'middle':
                if (!onlyStart && currentPage >= middleOffset && currentPage < totalPagesResults - middleOffset) {
                    if (currentPage < maxPages - middleOffset) {
                        rangeResult = range(middleOffset - 1, middleOffset + maxPages - 3);
                    } else {
                        rangeResult = range(currentPage - middleOffset + 1, currentPage + middleOffset);
                    }
                }

                break;

            case 'end':
                if (!onlyStart) {
                    if (currentPage > totalPagesResults - middleOffset - 1) {
                        rangeResult = range(totalPagesResults - maxPageOffset + 3, totalPagesResults + 1);
                    } else if (currentPage < totalPagesResults - middleOffset + maxPages + 3 || totalPagesLimited) {
                        rangeResult = [totalPagesResults];
                    }
                } else {
                    rangeResult = [];
                }

                break;
        }

        return rangeResult;
    }

    handleClick = (event) => {
        const {onClick} = this.props;

        event && event.preventDefault && event.preventDefault();

        onClick(Number(event.target.value));
    };

    renderPageButton = (pageNumber, _key) => (
        <li key={uniqueId()}>
            <Button {...this.getButtonProps(pageNumber)}>{pageNumber}</Button>
        </li>
    );

    renderSeparator(separatorShouldRender) {
        return separatorShouldRender ? <li aria-hidden="true" className="ellipsis" key={uniqueId()} /> : null;
    }

    render() {
        const {children = 'Pages:', totalPages} = this.props;
        const startPageRange = this.getRange('start');
        const middlePageRange = this.getRange('middle');
        const endPageRange = this.getRange('end');
        let content = null;

        if (totalPages > 1) {
            content = (
                <div className={this.getClass()}>
                    {children}
                    <ul aria-label="Pagination" className="pagination--list" role="navigation">
                        {map(startPageRange, this.renderPageButton)}
                        {this.renderSeparator(middlePageRange.length)}
                        {map(middlePageRange, this.renderPageButton)}
                        {this.renderSeparator(endPageRange.length)}
                        {map(endPageRange, this.renderPageButton)}
                    </ul>
                </div>
            );
        }

        return content;
    }
}

Pagination.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    currentPage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    inline: PropTypes.bool,
    maxPages: PropTypes.number,
    maxResultPages: PropTypes.number,
    onClick: PropTypes.func,
    totalPages: PropTypes.number.isRequired,
};
